import React, { useState, useEffect, ReactElement } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { actions as projectActions } from '../../actions/projectActions';
import { actions as projectMgrDashboardActions } from '../../actions/projectMgrDashboardActions';
import { useDispatch, useSelector } from 'react-redux';
import * as types from '../../constants/actionTypes';
import Notification from '../Common/Notification';
import type { Dispatch } from 'redux';
import { GlobalStyle, PageWrapper, ProjectOwnerName, ProjectName, ProjectHeader, BreadCrumb, DashboardsTab } from './styles';
import Tile from './components/Tile/Tile';
import Routes from '../../constants/routes';
import TasksAndMilestone from './components/TasksAndMilestone/TasksAndMilestone';
import ProjectDetails from './components/ProjectDetails/ProjectDetails';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import DocumentTitle from 'react-document-title';
import ProjectFiles from './components/Files/ProjectFiles';
import ProjectWaivers from './components/Waivers/ProjectWaivers';
import ProjectSupportTickets from './components/Support/ProjectSupportTickets';
import { isValidUrl } from '../../helpers/validation';
import { DEFAULT_PAGE_SIZE } from '../../constants/constants';
import Locations from './components/Locations/Locations';
import ProjectTasks from './components/ProjectTasks/ProjectTasks';
import { Dropdown } from 'primereact/dropdown';
import InputText from '@Common/InputText';
import Toggle from '../Common/Toggle/Toggle';

interface Project {
	id: number;
	name: string;
	abbreviation: string;
	description: string;
	projectManagerId: number;
	isActive: boolean;
	projectKey: string;
	supportAgentId: number;
	projectManager: ProjectManager;
}

interface ProjectManager {
	name: string;
}

interface TileValue {
	total: number;
	subtotal: number;
}

interface TaskData {
	id: string;
	value: number;
}

const PMDashboard = (): ReactElement => {
	const [projectData, setProjectData] = useState<Project>({
		id: -1,
		name: '',
		projectManager: { name: '' },
	});
	const [coachTasks, setCoachTasks] = useState<TileValue>();
	const [certifiedTasks, setCertifiedTasks] = useState<TileValue>();
	const [allTasks, setAllTasks] = useState<Array<TaskData>>([]);
	const [milestoneTasks, setMilestoneTasks] = useState<Array<TaskData>>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [activeTab, setActiveTab] = useState<number>(1);
	const [newExperienceState, setNewExperienceState] = useState<boolean>(true);
	const [selectedItem, setSelectedItem] = useState(null); // For single selection favorites
	const [searchTerm, setSearchTerm] = useState(''); // For filter ing favorites
	const { projectKey } = useParams<{ projectKey: string }>();
	const { currentSubscriptionId: subscriptionId, windowWidth, customerRole } = useSelector((state) => state.user);
	const newProject = {
		id: 0,
		name: '',
		description: '',
		abbreviation: '',
		canRequestProjectWaiver: false,
		isActive: true,
		subscriptionId: subscriptionId,
		projectManagerId: '',
		projectManagerName: '',
		supportAgentId: 0,
		supportAgentName: '',
		resources: [
			{
				id: 0,
				name: '',
				url: '',
				isActive: true,
				ordinal: 0,
			},
		],
		projectEpicLabels: [
			{
				id: 0,
				name: '',
				isActive: true,
				ordinal: 0,
			},
		],
		dateInactive: null,
		createdBy: '',
		createdDate: null,
		projectTagIds: [],
		zeroDateName: '',
	};

	const [projectDetailsState, setProjectDetailsState] = useState({
		currentUserId: useSelector((state) => state.user.id),
		projects: [],
		projectManagers: [],
		supportAgents: [],
		projectTags: [],
		totalCount: 0,
		isProjectWaiversActive: false,
		isProjectReferenceDatesActive: false,
		isFetching: false,
		isSaving: false,
		selectedProject: newProject,
		editProject: newProject,
		newProject: newProject,
		drawerOpen: false,
		isCopyProjectDrawerOpen: false,
		isFormValid: true,
		searchParams: {
			subscriptionId: subscriptionId,
			isActive: true,
			projectName: '',
			abbreviation: '',
			description: '',
			projectManagerIds: [],
			pageSize: DEFAULT_PAGE_SIZE,
			pageNumber: 1,
			sortBy: 'name',
			sortDir: 'asc',
		},
		isAddNewTagModalLoading: false,
		newProjectTag: {
			name: '',
			color: '#509823',
			bgColor: '#5F0DF0',
		},
		isNewTagFormValid: true,
		dashboardLocationsCols: [],
		dashboardTasksCols: [],
		projectOptionsDropdownList: [],
		userSelectedFavoriteProjects: [],
	});

	const dispatch: Dispatch = useDispatch();

	const loadProject = async (projectKey: string) => dispatch(projectActions.getProject(projectKey));
	const loadCoachTasks = async (projectKey: string) => dispatch(projectMgrDashboardActions.loadProjectCoachTasksGraph(projectKey));
	const loadCertifiedTasks = async (projectKey: string) => dispatch(projectMgrDashboardActions.loadProjectCertifiedTasksGraph(projectKey));
	const loadAllTasks = async (projectKey: string) => dispatch(projectMgrDashboardActions.loadAllProjectTasksGraph(projectKey));
	const loadMilestoneTasks = async (projectKey: string) => dispatch(projectMgrDashboardActions.loadProjectMilestoneTasksGraph(projectKey));
	const loadProjectListFilters = async () => dispatch(projectActions.loadProjectListFilters(subscriptionId));
	const saveProjectAsync = async (project) => dispatch(projectActions.saveProject(project));
	const saveLocationsColumns = async (params) => dispatch(projectMgrDashboardActions.saveLocationsColumns(params));
	const saveTasksColumns = async (params) => dispatch(projectMgrDashboardActions.saveTasksColumns(params));
	const saveUserFavoriteProjectAsync = async (favoriteProjects) => dispatch(projectActions.saveUserFavoriteProject(favoriteProjects, projectDetailsState.userSelectedFavoriteProjects));

	const [selectedUserProjects, setSelectedUserProjects] = useState([]);

	const loadFormFilters = async () => {
		try {
			setProjectDetailsState((prevState) => ({
				...prevState,
				isFetching: true,
			}));

			const result = await loadProjectListFilters();
			const payload = result.payload;
			if (result.type === types.LOAD_PROJECT_FILTERS_SUCCESS) {

				const addNewTagOption = {
					id: -9999,
					name: '+ Add New Project Tag',
					styles: {
						backgroundColor: '#F7F5FF',
						color: '#46389E',
					},
				};

				setProjectDetailsState((prevState) => ({
					...prevState,
					projectManagers: payload.projectManagers,
					supportAgents: payload.supportAgents,
					totalCount: payload.totalCount,
					isProjectWaiversActive: payload.isProjectWaiversActive,
					IsProjectReferenceDatesActive: payload.isProjectReferenceDatesActive,
					projectTags: [...payload.tags, addNewTagOption],
					dashboardLocationsCols: payload.dashboardLocationsCols,
					dashboardTasksCols: payload.dashboardTasksCols,
					projectOptionsDropdownList: [...payload.projectOptionsDropdownList],
					userSelectedFavoriteProjects: [...payload.userFavoriteProjects],
				}));
			} else if (result.type === types.LOAD_PROJECT_FILTERS_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		} finally {
			setProjectDetailsState((prevState) => ({
				...prevState,
				isFetching: false,
			}));
		}
	};

	const save = async () => {
		const project = projectDetailsState.editProject;
		// remove all blank resources
		if (project.resources) {
			project.resources = project.resources.filter((x) => x?.name?.trim().length > 0 && x?.url?.trim().length > 0);
		}

		if (project.projectEpicLabels) {
			project.projectEpicLabels = project.projectEpicLabels.filter((x) => x?.name?.trim().length > 0);
		}
		const result = await saveProjectAsync(project);

		if (result.type === types.CREATE_PROJECT_SUCCESS || result.type === types.UPDATE_PROJECT_SUCCESS) {
			Notification('success', 'Save Successful', 'Project saved successfully');
			return true;
		} else if (result.type === types.UPDATE_PROJECT_FAILURE || result.type === types.CREATE_PROJECT_FAILURE) {
			setProjectDetailsState((prevState) => ({
				...prevState,
				errorMessage: result.payload.message,
			}));
			Notification('error', 'An Error Occurred', result.payload.message);
			return false;
		}
	};

	const history = useHistory();

	const clickBack = () => {
		history.push(Routes.AdminHome + '/projects');
	};

	const formatProjectData = async () => {
		try {
			const result = await loadProject(projectKey);
			const payload = result.payload;
			if (result.type === types.GET_PROJECT_SUCCESS) {
				setProjectData(payload.project);
				setProjectDetailsState((prevState) => ({
					...prevState,
					editProject: payload.project,
				}));
			} else if (result.type === types.GET_PROJECT_FAILURE) {
				Notification('error', 'An Error Occurred', payload.message);
			}
		} catch (error) {
			Notification('error', 'An Error Occurred', error);
		}
	};

	const formatProjectCoachtasks = async () => {
		try {
			const response = await loadCoachTasks(projectKey);
			if (response.type === types.LOAD_PROJECT_COACH_TASKS_GRAPH_SUCCESS) {
				const totalTasks = calculateTotalTasks(response.payload.coachTasks.data);
				const completedTasks = calculateCompletedTasks(response.payload.coachTasks.data);

				setCoachTasks({ total: totalTasks, subtotal: completedTasks });
			} else if (response.type === types.LOAD_PROJECT_COACH_TASKS_GRAPH_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	const calculateTotalTasks = (data: Array<TaskData>): number => {
		return data.reduce((acc, item) => acc + item.value, 0);
	};

	const calculateCompletedTasks = (data: Array<TaskData>): number => {
		const completeItem = data.find((item) => item.id === 'Complete');
		const completeValue = completeItem ? completeItem.value : 0;
		return completeValue;
	};

	const formatCertifiedTasks = async () => {
		try {
			const response = await loadCertifiedTasks(projectKey);
			if (response.type === types.LOAD_PROJECT_CERTIFIED_TASKS_GRAPH_SUCCESS) {
				const totalTasks = calculateTotalTasks(response.payload.certifiedTasks.data);
				const completedTasks = calculateCompletedTasks(response.payload.certifiedTasks.data);

				setCertifiedTasks({ total: totalTasks, subtotal: completedTasks });
			} else if (response.type === types.LOAD_PROJECT_CERTIFIED_TASKS_GRAPH_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	const formatAllTasks = async () => {
		try {
			const response = await loadAllTasks(projectKey);
			if (response.type === types.LOAD_PROJECT_ALL_TASKS_GRAPH_SUCCESS) {
				setAllTasks(response.payload.allProjectTasks.data);
			} else if (response.type === types.LOAD_PROJECT_ALL_TASKS_GRAPH_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	const formatMilestoneTasks = async () => {
		try {
			const response = await loadMilestoneTasks(projectKey);
			if (response.type === types.LOAD_PROJECT_MILESTONE_TASKS_GRAPH_SUCCESS) {
				setMilestoneTasks(response.payload.projectMilestoneTasks.data);
			} else if (response.type === types.LOAD_PROJECT_MILESTONE_TASKS_GRAPH_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	const saveUserFavoriteProjectAsyncHandler = async (userFavoriteProjects) => {
		console.log('saveUserFavoriteProjectAsyncHandler');
		console.log(userFavoriteProjects);
		try {
			const response = await saveUserFavoriteProjectAsync(userFavoriteProjects);
			if (response.type === types.POST_SAVE_FAVORITE_PROJECT_SUCCESS) {
				Notification('success', 'Save Successful', 'Project favorites saved successfully');
			} else if (response.type === types.POST_SAVE_FAVORITE_PROJECT_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	const handleTabClick = (tabId: number) => {
		setActiveTab(tabId);
	};

	const onFormChange = (name: string, value: any) => {
		setProjectDetailsState((prevState) => ({
			...prevState,
			editProject: { ...projectDetailsState.editProject, [name]: value },
		}));
	};

	const saveClick = async (e: React.FormEvent) => {
		e.preventDefault();

		const { name, abbreviation, projectManagerId, resources } = projectDetailsState.editProject;

		const urlsValid = urlsIsValid(resources);
		const isFormValid = urlsValid && name.trim().length > 0 && abbreviation.trim().length > 0 && projectManagerId !== '';

		setProjectDetailsState((prevState) => ({
			...prevState,
			editProject: {
				...prevState.editProject,
				isFormValid,
			},
		}));

		if (isFormValid) {
			if (await save()) {
				await formatProjectData();
			}
		}
	};

	const urlsIsValid = (resources) => {
		if (!resources || resources.length === 0) {
			return true;
		}

		return resources.every((r) => r.url === '' || isValidUrl(r.url));
	};

	const onToggleChange = (name, value) => {
		console.log(name, value)
		history.push(Routes.ProjectManagerDashboard + `/${projectKey}`);
	}

	const onLocationsColumnChange = async (newColumns: Array<string>) => {
		// Save columns personalization
		await saveLocationsColumns({ columns: newColumns });

		setProjectDetailsState((prevState) => ({
			...prevState,
			dashboardLocationsCols: newColumns,
		}));
	};

	const onTasksColumnChange = async (newColumns: Array<string>) => {
		// Save columns personalization
		await saveTasksColumns({ columns: newColumns });

		setProjectDetailsState((prevState) => ({
			...prevState,
			dashboardTasksCols: newColumns,
		}));
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				await Promise.all([
					formatProjectData(),
					formatProjectCoachtasks(),
					formatCertifiedTasks(),
					formatAllTasks(),
					formatMilestoneTasks(),
					loadFormFilters(),
				]);

				if (projectDetailsState.userSelectedFavoriteProjects) {
					setSelectedUserProjects(projectDetailsState.userSelectedFavoriteProjects);
				}
			} catch (error) {
				Notification('error', 'An Error Occurred', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, []);

	const tabs = [
		{
			id: 1,
			label: 'Locations + Teams',
			component: <Locations projectData={projectData} subscriptionId={subscriptionId} userColumns={projectDetailsState.dashboardLocationsCols} onColumnsChange={onLocationsColumnChange} />,
			icon: 'fa-location-dot',
		},
		{
			id: 2,
			label: 'Task Lists',
			component: <ProjectTasks projectData={projectData} subscriptionId={subscriptionId} userColumns={projectDetailsState.dashboardTasksCols} onColumnsChange={onTasksColumnChange} />,
			icon: 'fa-square-list',
		},
		{
			id: 3,
			label: 'Project Files',
			component: <ProjectFiles subscriptionId={subscriptionId} projectKey={projectKey} windowWidth={windowWidth} />,
			icon: 'fa-files',
		},
		{
			id: 4,
			label: 'Waiver Requests',
			component: (
				<ProjectWaivers
					subscriptionId={subscriptionId}
					projectId={projectData.id}
					projectKey={projectKey}
					windowWidth={windowWidth}
					customerRole={customerRole}
				/>
			),
			icon: 'fa-file-lines',
		},
		{
			id: 5,
			label: 'Project Support',
			component: <ProjectSupportTickets subscriptionId={subscriptionId} projectId={projectData.id} windowWidth={windowWidth} />,
			icon: 'fa-messages',
		},
		{
			id: 6,
			label: 'Project Details',
			component: (
				<ProjectDetails projectDetails={projectDetailsState} onFormChange={onFormChange} onSubmit={saveClick}></ProjectDetails>
			),
			icon: 'fa-info-circle',
		},
	];

	const itemTemplateFavorite = (option) => {
		const isSelected = selectedUserProjects.some((i) => i.id === option.id);

		return (
			<span
				className="p-dropdown-item-label"
				style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
			>
				<span style={{ fontFamily: 'Greycliff', fontWeight: '400', fontSize: '12px' }}>{option.name}</span> {/* Ensures it takes available space */}
				<span
					className='ms-2'
					style={{ cursor: 'pointer', fontSize: '18px', marginRight: '0px' }}
					onClick={(e) => {
						e.stopPropagation();
						toggleFavoriteProject(option);

					}}
				>
					{isSelected ? (
						<i className="fa-regular fa-star" style={{ color: "gold" }}></i>
					) : (
						<i className="fa-regular fa-star" style={{ color: "gray" }}></i>
					)}
				</span>
			</span>
		);
	};

	const toggleFavoriteProject = async (item) => {
		const updatedProjects = (prev) =>
			prev.some((i) => i.id === item.id)
				? prev.filter((i) => i.id !== item.id) // Remove if already selected
				: [...prev, item]; // Add if not selected

		setSelectedUserProjects(updatedProjects);

		await new Promise((resolve) => setTimeout(resolve, 0));

		await saveUserFavoriteProjectAsyncHandler(updatedProjects(selectedUserProjects));
	};

	const dropdownHeader = (
		<InputText
			type="text"
			name='searchFavorite'
			value={searchTerm}
			onChange={(e) => setSearchTerm(e.target.value)}
			placeholder="Search..."
			className="w-full"
		/>
	);

	return (
		<LoadingIndicator isLoading={isLoading} hideWhileLoading={true} isFullscreen={true}>
			<DocumentTitle title='Project Dashboard' />
			<PageWrapper>
				<GlobalStyle />
				<div className='row'>
					<div className='col-10'>
						<a onClick={() => clickBack()}>
							<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'>
								<path
									d='M9.6126 13.5C9.50961 13.5005 9.40755 13.4804 9.31243 13.4409C9.21731 13.4014 9.13103 13.3433 9.05865 13.2701L3.83277 8.04416C3.68597 7.89718 3.60352 7.69795 3.60352 7.49022C3.60352 7.28249 3.68597 7.08325 3.83277 6.93628L9.05865 1.71039C9.20725 1.57192 9.40379 1.49654 9.60687 1.50012C9.80995 1.50371 10.0037 1.58597 10.1473 1.72959C10.291 1.87322 10.3732 2.06698 10.3768 2.27005C10.3804 2.47313 10.305 2.66968 10.1665 2.81827L5.4946 7.49022L10.1665 12.1622C10.3133 12.3091 10.3958 12.5084 10.3958 12.7161C10.3958 12.9238 10.3133 13.1231 10.1665 13.2701C10.0942 13.3433 10.0079 13.4014 9.91277 13.4409C9.81764 13.4804 9.71559 13.5005 9.6126 13.5Z'
									fill='#6C757D'
								/>
							</svg>
							<BreadCrumb>Projects</BreadCrumb>
						</a>
					</div>
					<div className="col-2 d-flex justify-content-end">
						<Toggle name="isActive" text="New Experience" checked={newExperienceState} onChange={onToggleChange} />
					</div>
				</div>
				<div className='row'>
					<div className='col-md-12'>
						<ProjectHeader>

							<div className="left-group multiselect-favorites">
								{(!isLoading && !projectDetailsState.isFetching) &&
									<Dropdown
										value={selectedUserProjects}
										options={projectDetailsState.projectOptionsDropdownList} // Use filtered options
										onChange={(e) => setSelectedItem(e.value)} // Need to define what to do with this
										optionLabel='name'
										placeholder=''
										className='w-full flex justify-between items-center w-full'
										showClear
										filter // Enable built-in filter
										filterBy="name"
										style={{ border: 'none', justifyContent: 'space-between' }}
										panelheadertemplate={dropdownHeader}
										itemTemplate={itemTemplateFavorite}
									/>
								}
								<ProjectName>{projectData?.name}</ProjectName>
							</div>
							<ProjectOwnerName>
								<span>{projectData?.projectManager.name}</span>
								<span>Project Owner</span>
							</ProjectOwnerName>
						</ProjectHeader>
					</div>
				</div>
				<div className='row'>
					<div className='col-md-6 col-lg-3 col-xl-2'>
						<Tile
							title='Certified Complete'
							subtitle='Teams who have signed off on project completion'
							total={certifiedTasks?.total}
							subtotal={certifiedTasks?.subtotal}></Tile>
					</div>
					<div className='col-md-12 col-lg-6 col-xl-8'>
						<TasksAndMilestone
							title='Task and Milestone Completion'
							subtitle='A summary of all projects tasks within published locations'
							allTasks={allTasks}
							milestoneTasks={milestoneTasks}></TasksAndMilestone>
					</div>
					<div className='col-md-6 col-lg-3 col-xl-2'>
						<Tile
							title='Coach Assigned'
							subtitle='Team-specific individual resposible for leading the project'
							total={coachTasks?.total}
							subtotal={coachTasks?.subtotal}></Tile>
					</div>
				</div>
				<div className='row g-0'>
					<DashboardsTab className='nav nav-tabs mt-5 w-100'>
						{tabs.map((tab) => (
							<li key={tab.id} className='nav-item'>
								<a
									className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
									onClick={() => handleTabClick(tab.id)}
									type='button'>
									<i className={`fa-regular ${tab.icon}`} />
									<span className='tab-label'>{tab.label}</span>
								</a>
							</li>
						))}
					</DashboardsTab>
					<div className='col-12'>{tabs.find((tab) => tab.id === activeTab)?.component}</div>
				</div>
			</PageWrapper>
		</LoadingIndicator>
	);
};

export default PMDashboard;
